import React, { useEffect, useState } from 'react';
import { Redirect, withRouter, RouteComponentProps } from 'react-router-dom';
import { Alert, Button, Card, TextField } from '@betareader/design-system';
import queryString from 'query-string';

import fire from '../../config/fire';
import { getVerificationReturnPath } from './verificationReturnPath';

type VerifyEmailWallProps = RouteComponentProps;

export const VerifyEmailWall: React.FC<VerifyEmailWallProps> = ({
  location,
}) => {
  const parsed = queryString.parse(location.search);
  const redirectParam =
    typeof parsed.redirect === 'string' ? parsed.redirect : '';
  const redirectPath = getVerificationReturnPath(redirectParam);
  const [authReady, setAuthReady] = useState(false);
  const [signedIn, setSignedIn] = useState(false);

  const [verified, setVerified] = useState<boolean>(
    !!fire.auth().currentUser && !!fire.auth().currentUser?.emailVerified
  );
  const [currentEmail, setCurrentEmail] = useState<string>(
    fire.auth().currentUser?.email || ''
  );
  const [resendStatus, setResendStatus] = useState<
    'idle' | 'sending' | 'sent' | 'error'
  >('idle');
  const [resendError, setResendError] = useState<string>('');

  const [showChangeEmail, setShowChangeEmail] = useState<boolean>(false);
  const [newEmail, setNewEmail] = useState<string>('');
  const [changeStatus, setChangeStatus] = useState<
    'idle' | 'sending' | 'sent' | 'error'
  >('idle');
  const [changeError, setChangeError] = useState<string>('');

  useEffect(() => {
    let active = true;

    const check = async () => {
      const user = fire.auth().currentUser;
      if (!user) return;
      try {
        await user.reload();
        if (!active) return;
        setCurrentEmail(user.email || '');
        if (user.emailVerified) {
          // Force a fresh ID token so the next API request carries
          // email_verified=true; without this the cached token still
          // reports false until natural expiry (~1h).
          const freshToken = await user.getIdToken(true);
          localStorage.setItem('br-storage-key', freshToken);
          setVerified(true);
        }
      } catch (e) {
        // ignore transient reload errors
      }
    };

    const unsubscribe = fire.auth().onAuthStateChanged((user: any) => {
      if (!active) return;
      setAuthReady(true);
      setSignedIn(!!user && !user.isAnonymous);
      setVerified(false);
      setCurrentEmail(user?.email || '');
      if (user && !user.isAnonymous) check();
    });
    const interval = window.setInterval(check, 5000);
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);

    return () => {
      active = false;
      unsubscribe();
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const actionCodeSettings = {
    url: `${window.location.origin}${redirectPath}`,
    handleCodeInApp: false,
  };

  const handleResend = async () => {
    const user = fire.auth().currentUser;
    if (!user) return;
    setResendStatus('sending');
    setResendError('');
    try {
      await user.sendEmailVerification(actionCodeSettings);
      setResendStatus('sent');
    } catch (e: any) {
      setResendStatus('error');
      setResendError(e?.message || 'Failed to send verification email.');
    }
  };

  const handleChangeEmail = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const user = fire.auth().currentUser;
    if (!user) return;
    if (!newEmail || newEmail === user.email) return;
    setChangeStatus('sending');
    setChangeError('');
    try {
      // Safe path: sends verification to the NEW address; Firebase only
      // swaps the account email after the user clicks the link, so a typo
      // in the new address does not lock them out.
      await user.verifyBeforeUpdateEmail(newEmail, actionCodeSettings);
      setChangeStatus('sent');
    } catch (e: any) {
      setChangeStatus('error');
      setChangeError(e?.message || 'Failed to update email address.');
    }
  };

  if (!authReady) return <p role="status">Checking your account…</p>;

  if (!signedIn) {
    const returnUrl = `/verify-email?redirect=${encodeURIComponent(redirectPath)}`;
    return (
      <Redirect to={`/signin?redirect=${encodeURIComponent(returnUrl)}`} />
    );
  }

  if (verified) {
    return <Redirect to={redirectPath} />;
  }

  return (
    <main style={{ maxWidth: 500, margin: '2em auto', padding: '0 16px' }}>
      <a href="/mymanuscripts" aria-label="BetaReader home">
        <img
          src="/assets/images/BR-orange-horizontal.png"
          alt="BetaReader"
          style={{ maxWidth: '100%', marginBottom: '1em' }}
        />
      </a>
      <Card style={{ marginBottom: 16, padding: 24 }}>
        <h1>Please confirm your email</h1>
        <p>
          Confirm <strong>{currentEmail}</strong> to continue reading. If your
          link has expired, request a new one below and open the latest
          verification email.
        </p>
        {resendStatus === 'sent' && (
          <Alert variant="success">
            New verification link sent to <strong>{currentEmail}</strong>. Open
            the latest email. If it does not arrive, check your spam folder.
          </Alert>
        )}
        {resendStatus === 'error' && (
          <Alert variant="error">{resendError}</Alert>
        )}
        <Button
          aria-busy={resendStatus === 'sending'}
          disabled={resendStatus === 'sending'}
          onClick={handleResend}
        >
          Resend verification email
        </Button>
      </Card>

      <Card style={{ padding: 24 }}>
        {!showChangeEmail && (
          <p>
            Typed the wrong address?{' '}
            <a
              href="#change-email"
              onClick={e => {
                e.preventDefault();
                setShowChangeEmail(true);
                setNewEmail('');
                setChangeStatus('idle');
              }}
            >
              Change email address
            </a>
            .
          </p>
        )}
        {showChangeEmail && changeStatus !== 'sent' && (
          <>
            <h2>Change email address</h2>
            <p>
              We will send a confirmation link to the new address. Your current
              email <strong>{currentEmail}</strong> stays active until you click
              that link.
            </p>
            <form onSubmit={handleChangeEmail}>
              <TextField
                id="verification-new-email"
                label="New email address"
                type="email"
                placeholder="new@example.com"
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                required
              />
              {changeStatus === 'error' && (
                <Alert variant="error">{changeError}</Alert>
              )}
              <Button
                type="submit"
                aria-busy={changeStatus === 'sending'}
                disabled={
                  changeStatus === 'sending' ||
                  !newEmail ||
                  newEmail === currentEmail
                }
              >
                Send confirmation link
              </Button>
              <Button
                variant="secondary"
                onClick={e => {
                  e.preventDefault();
                  setShowChangeEmail(false);
                }}
              >
                Cancel
              </Button>
            </form>
          </>
        )}
        {changeStatus === 'sent' && (
          <Alert variant="success">
            Confirmation link sent to <strong>{newEmail}</strong>. Click it to
            finish the change; your current email stays active until you do.
          </Alert>
        )}
      </Card>
    </main>
  );
};

export default withRouter(VerifyEmailWall);
